import {Block} from './blocks/Block';
import {BlocksPanel} from './blocks/BlocksPanel';
import {ContextAction} from './blocks/ContextAction';
import {ConstructorOfType} from './ConstructorOfType';
import {BuiltInControl} from './controls/BuiltInControl';
import {Control} from './controls/Control';
import {GeneralPanelTab} from './controls/GeneralPanelTab';
import {ModulesPanelTab} from './controls/ModulesPanelTab';
import {SettingsPanelRegistry} from './controls/SettingsPanelRegistry';
import {ExternalAiAssistant} from './ExternalAiAssistant';
import {ExternalDisplayConditionsLibrary} from './ExternalDisplayConditionsLibrary';
import {ExternalImageLibrary} from './ExternalImageLibrary';
import {ExternalImageLibraryTab} from './ExternalImageLibraryTab';
import {ExternalSmartElementsLibrary} from './ExternalSmartElementsLibrary';
import {ExternalVideosLibrary} from './ExternalVideoLibrary';
import {IconsRegistry} from './icons/IconsRegistry';
import {UIElement} from './ui-elements/UIElement';
import {UIElementTagRegistry} from './ui-elements/UIElementTagRegistry';

interface ExtensionOptions {
  i18n: Record<string, Record<string, string>> | undefined;
  styles: string | undefined;
  previewStyles?: string;
  uiElements: ConstructorOfType<UIElement>[];
  generalPanelTabs: ConstructorOfType<GeneralPanelTab>[];
  modulesPanelTabs: ConstructorOfType<ModulesPanelTab>[];
  controls: ConstructorOfType<Control | BuiltInControl>[];
  contextActions: ConstructorOfType<ContextAction>[];
  blocks: ConstructorOfType<Block>[];
  uiElementTagRegistry: ConstructorOfType<UIElementTagRegistry> | undefined;
  settingsPanelRegistry: ConstructorOfType<SettingsPanelRegistry> | undefined;
  externalSmartElementsLibrary?: ConstructorOfType<ExternalSmartElementsLibrary>;
  externalImageLibrary?: ConstructorOfType<ExternalImageLibrary>;
  externalAiAssistant?: ConstructorOfType<ExternalAiAssistant>;
  externalDisplayConditionsLibrary?: ConstructorOfType<ExternalDisplayConditionsLibrary>;
  externalVideoLibrary?: ConstructorOfType<ExternalVideosLibrary>;
  blocksPanel?: ConstructorOfType<BlocksPanel>;
  iconsRegistry?: ConstructorOfType<IconsRegistry>;
  externalImageLibraryTab?: ConstructorOfType<ExternalImageLibraryTab>;
}

export class Extension {
  private readonly i18n?: Record<string, Record<string, string>>;
  private readonly styles?: string;
  private readonly previewStyles?: string;
  private readonly uiElements: ConstructorOfType<UIElement>[] = [];
  private readonly uiElementTagRegistry?: ConstructorOfType<UIElementTagRegistry>;
  private readonly controls: ConstructorOfType<Control | BuiltInControl>[] = [];
  private readonly settingsPanelRegistry?: ConstructorOfType<SettingsPanelRegistry>;
  private readonly contextActions: ConstructorOfType<ContextAction>[] = [];
  private readonly blocks: ConstructorOfType<Block>[] = [];
  private readonly generalPanelTabs: ConstructorOfType<GeneralPanelTab>[] = [];
  private readonly modulesPanelTabs: ConstructorOfType<ModulesPanelTab>[] = [];
  private readonly id: string;
  private readonly externalSmartElementsLibrary?: ConstructorOfType<ExternalSmartElementsLibrary>;
  private readonly externalImageLibrary?: ConstructorOfType<ExternalImageLibrary>;
  private readonly externalImageLibraryTab?: ConstructorOfType<ExternalImageLibraryTab>;
  private readonly externalAiAssistant?: ConstructorOfType<ExternalAiAssistant>;
  private readonly externalDisplayConditionsLibrary?: ConstructorOfType<ExternalDisplayConditionsLibrary>;
  private readonly externalVideoLibrary?: ConstructorOfType<ExternalVideosLibrary>;
  private readonly blocksPanel?: ConstructorOfType<BlocksPanel>;
  private readonly iconsRegistry?: ConstructorOfType<IconsRegistry>;
  constructor(options: ExtensionOptions) {
    this.i18n = options?.i18n;
    this.styles = options?.styles;
    this.previewStyles = options?.previewStyles;
    this.uiElements = options?.uiElements ?? [];
    this.uiElementTagRegistry = options?.uiElementTagRegistry;
    this.controls = options?.controls ?? [];
    this.settingsPanelRegistry = options?.settingsPanelRegistry;
    this.contextActions = options?.contextActions ?? [];
    this.blocks = options?.blocks ?? [];
    this.generalPanelTabs = options?.generalPanelTabs ?? [];
    this.modulesPanelTabs = options?.modulesPanelTabs ?? [];
    this.externalSmartElementsLibrary = options?.externalSmartElementsLibrary;
    this.externalImageLibrary = options?.externalImageLibrary;
    this.externalImageLibraryTab = options?.externalImageLibraryTab;
    this.externalAiAssistant = options?.externalAiAssistant;
    this.externalDisplayConditionsLibrary = options?.externalDisplayConditionsLibrary;
    this.externalVideoLibrary = options?.externalVideoLibrary;
    this.blocksPanel = options?.blocksPanel;
    this.iconsRegistry = options?.iconsRegistry;
    // generate random string for id
    this.id = Math.random().toString(36).substring(2);
  }

  public getI18n(): Record<string, Record<string, string>> | undefined {
    return this.i18n;
  }

  public getStyles(): string | undefined {
    return this.styles;
  }

  public getPreviewStyles(): string | undefined {
    return this.previewStyles;
  }

  public getUiElements(): ConstructorOfType<UIElement>[] {
    return this.uiElements;
  }

  public getUiElementTagRegistry(): ConstructorOfType<UIElementTagRegistry> | undefined {
    return this.uiElementTagRegistry;
  }

  public getControls(): ConstructorOfType<Control | BuiltInControl>[] {
    return this.controls;
  }

  public getSettingsPanelRegistry(): ConstructorOfType<SettingsPanelRegistry> | undefined {
    return this.settingsPanelRegistry;
  }

  public getContextActions(): ConstructorOfType<ContextAction>[] {
    return this.contextActions;
  }

  public getBlocks(): ConstructorOfType<Block>[] {
    return this.blocks;
  }

  public getId(): string {
    return this.id;
  }

  public getExternalSmartElementsLibrary(): ConstructorOfType<ExternalSmartElementsLibrary> | undefined {
    return this.externalSmartElementsLibrary;
  }

  public getExternalImageLibrary(): ConstructorOfType<ExternalImageLibrary> | undefined {
    return this.externalImageLibrary;
  }

  public getExternalImageLibraryTab(): ConstructorOfType<ExternalImageLibraryTab> | undefined {
    return this.externalImageLibraryTab;
  }

  public getExternalAiAssistant(): ConstructorOfType<ExternalAiAssistant> | undefined {
    return this.externalAiAssistant;
  }

  public getExternalDisplayConditionsLibrary(): ConstructorOfType<ExternalDisplayConditionsLibrary> | undefined {
    return this.externalDisplayConditionsLibrary;
  }

  public getExternalVideoLibrary(): ConstructorOfType<ExternalVideosLibrary> | undefined {
    return this.externalVideoLibrary;
  }

  public getBlocksPanel(): ConstructorOfType<BlocksPanel> | undefined {
    return this.blocksPanel;
  }

  public getIconsRegistry(): ConstructorOfType<IconsRegistry> | undefined {
    return this.iconsRegistry;
  }

  public getGeneralPanelTabs(): ConstructorOfType<GeneralPanelTab>[] {
    return this.generalPanelTabs;
  }

  public getModulesPanelTabs(): ConstructorOfType<ModulesPanelTab>[] {
    return this.modulesPanelTabs;
  }
}
